import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import { RoutePaths } from '../../../../../config/route-paths';
import { LoadingPage } from '../../../../../lib/components/LoadingPage/LoadingPage';
import { IRoute } from '../../../routing/interfaces/Routing.types';
import { AuthenticationActions } from '../../state/authenticationActions';
import {
  selectAuthenticationTokens,
  selectIsCheckingAuthentication,
  selectIsLoggedIn,
  selectIsAuthorizedFromSupportPortal,
  selectPermissions,
} from '../../state/authenticationSelectors';
import { isUserPermitted } from '../../utils/isUserPermitted';
import { setPostLoginRedirectPath } from '../../utils/postLoginRedirectPath';

export interface AuthorizationGuardProps {
  route: IRoute;
  children?: React.ReactNode;
}

export const AuthorizationGuard: React.FC<AuthorizationGuardProps> = ({ route, children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const isCheckingAuthentication = useSelector(selectIsCheckingAuthentication);
  const authenticationTokens = useSelector(selectAuthenticationTokens);
  const userPermissions = useSelector(selectPermissions);
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const isPermittedToAccessRoute = isUserPermitted(userPermissions, route.permissions);
  const isAuthorizedFromSupportPortal = useSelector(selectIsAuthorizedFromSupportPortal);

  useEffect(() => {
    dispatch(AuthenticationActions.checkAuthentication());
  }, [dispatch]);

  useEffect(() => {
    if (isLoggedIn && authenticationTokens) {
      dispatch(
        AuthenticationActions.setUserInfo({
          decodedIdToken: authenticationTokens.decoded.idToken,
        })
      );
    }
  }, [isLoggedIn, authenticationTokens, dispatch]);

  if (isCheckingAuthentication) {
    return <LoadingPage />;
  }

  // comparing explicitly to false since isAuthorizedFromSupportPortal can be undefined for initial state
  // only false value is redirected unauthorized page
  if (isAuthorizedFromSupportPortal === false) {
    return <Navigate to={RoutePaths.UNAUTHORIZED} />;
  }

  if (!authenticationTokens) {
    // Save before redirect so the path survives Cognito and is available on /init.
    // Must be synchronous — a useEffect can race with feature-flag/terms setup on return.
    setPostLoginRedirectPath(`${location.pathname}${location.search}`);
    return <Navigate to={RoutePaths.LOGIN} />;
  }

  if (!isPermittedToAccessRoute) {
    return <Navigate to={RoutePaths.ROOT} />;
  }

  return <>{children}</>;
};
